import { useEffect, useRef } from 'react';
import useAsync from 'hooks/useAsync';
import RouteHelpers from 'helpers/routes';
import { Navigate } from 'react-router-dom';
import { useRouteParams } from 'hooks/useRouteParams';
import { getUserToken, respondTokenInvite } from 'actions/userTokens';

type URLParams = {
  token: string;
};

// Maps an accepted subscription invitation to the product's deep-link
// destination. Falls back to the dashboard when the product is unknown or its
// resource no longer exists.
function getRedirectUrl(userToken: any): string {
  const role = userToken?.payload?.role;

  const destinations: Record<string, () => string> = {
    learning_resources: () =>
      role === 'lr_coach'
        ? RouteHelpers.getPath('coach-learning-resources')
        : RouteHelpers.getPath('learning-resources'),
    data_collection: () => RouteHelpers.getPath('measure'),
    instant_insights: () =>
      role === 'ii_admin' || role === 'ii_coach'
        ? RouteHelpers.getPath('instant-insights-dashboard')
        : RouteHelpers.getPath('instant-insights'),
    class_connect: () => RouteHelpers.getPath('canvas')
  };

  const resolve = destinations[userToken?.subscription?.product_name];
  return resolve ? resolve() : '/';
}

/**
 * Subscription invitations are accepted automatically once the user has
 * authenticated from the email link (LOVE-203). This screen no longer asks the
 * user to confirm — it accepts the invitation and deep-links them straight into
 * the relevant product, falling back to the dashboard when there is no
 * product-specific destination (or the resource was deleted).
 */
function View() {
  const { token } = useRouteParams<URLParams>();
  const { run: loadToken, data: userToken, isError } = useAsync();
  const { run: acceptInvite } = useAsync();
  const hasAccepted = useRef(false);

  useEffect(() => {
    loadToken(getUserToken(token));
  }, [loadToken, token]);

  useEffect(() => {
    if (!userToken || !userToken.usable || hasAccepted.current) {
      return;
    }

    hasAccepted.current = true;
    acceptInvite(respondTokenInvite(userToken.token, 'accept'), {
      onSuccess: () => window.location.replace(getRedirectUrl(userToken)),
      onError: () => window.location.replace('/')
    });
  }, [userToken, acceptInvite]);

  if (isError || (userToken && !userToken.usable)) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className="text-center p-6">
      <i
        className="fa-solid fa-spinner animate-spin text-xl text-ds-text-primary"
        aria-label="Loading..."
      />
    </div>
  );
}

export default View;
